import type { TaskStatus } from './IScheduler.js';

export type ZoneType = 'storage' | 'equipment' | 'access' | 'safety';
export type ZoneStatus = 'active' | 'idle' | 'staging' | 'attention';

// Item/destination/instructions are user-entered free text passed through
// verbatim (not i18n catalog codes). Genba's delivery item has no supplier
// field; the supplier is D-ORCA-owned (`supplierId`, spec
// docs/specs/2026-10-25-delivery-suppliers-design.md). Quantity/unit are
// D-ORCA's own: Genba records no delivered amount, so without them "a delivery landed"
// could never answer "how much".)
export type Delivery = {
  ref: string;
  /** Record-creation instant (ISO datetime) — an audit stamp, like a stock
   *  snapshot's `enteredAt`; the DOMAIN "when was this requested" day is
   *  `requestedDate`. Null on rows predating the stamp. Convert with
   *  `zonedDay` before showing as a day. */
  createdAt: string | null;
  destinationName: string;
  /** What the truck carries — one entry per material, 1..`MAX_DELIVERY_LINES`,
   *  material names distinct within the order. Everything that belongs to a
   *  MATERIAL (its name, amount, unit, the tasks that booked it, whether it
   *  has been reconciled into stock) lives here; everything that belongs to
   *  the TRUCK stays on the order. */
  lines: IDeliveryLine[];
  deliveryOrder: string | null;
  requestedDate: string | null; // YYYY-MM-DD
  answeredDate: string | null; // YYYY-MM-DD
  /** The day this delivery is expected — a property of the ORDER, not of any
   *  one status. Any status can carry one, and the system prefills it from the
   *  material's lead time, so it is often computed rather than agreed. It used
   *  to be `confirmedDate`, which is where the old "agreed deadline" note came
   *  from. */
  dueDate: string | null; // YYYY-MM-DD
  /** Server-stamped arrival (ISO datetime) — the arrival FACT, and the sole
   *  answer to "has it arrived?". Stamped by moving the card into the Delivered
   *  system column and cleared by moving it back out; never client-written. */
  deliveredAt: string | null;
  /** Sync is the commit point — locked status + one-unsynced-line UI. The
   *  ORDER is synced once every one of its lines is. */
  isSynced: boolean;
  /** The kanban column this delivery sits in — the ONLY status a delivery has.
   *  Genba's six-state workflow enum is gone; everything else the UI says about
   *  a delivery (overdue, in flight, not sent yet) is derived from the dates. */
  status: string;
  /** The supplier this order is from, or null. One per ORDER, never per line.
   *  An id the site's supplier list no longer holds reads as no supplier. */
  supplierId: string | null;
  /** Starred as critical: leads its board column and the Critical orders tile. */
  isCritical: boolean;
};

/** A site's supplier: who a delivery order is from. Contact details are
 *  optional and stored as typed. */
export type Supplier = {
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
};

/** Upper bound on an order's lines — mirrors the backend's
 *  `MAX_DELIVERY_LINES`. The dialog stops offering "+ Add material" here. */
export const MAX_DELIVERY_LINES = 20;

/** One material on a truck. `id` is stable across edits, which is what lets a
 *  rename be a change rather than a remove-plus-add in the order's history. */
export interface IDeliveryLine {
  id: string;
  itemName: string;
  /** How much this LINE is for; null when not recorded (never 0). */
  quantity: number | null;
  /** Unit of `quantity`, from the shared logistics unit list (`../units`). */
  unit: string | null;
  /** Whether THIS material has been reconciled into stock. One unsynced line
   *  per material is the rule the backend's unique index enforces. */
  isSynced: boolean;
  /** The Scheduler tasks that booked THIS material, in joining order (the task
   *  that raised it first). Empty for a line raised anywhere but a task's Stock
   *  tab. Task ids repeat across schedules, so each link carries its schedule.
   *  D-ORCA-only: never read from or written to Genba. */
  sources: IDeliveryLineSource[];
}

/** A task link as posted on a join: which schedule, which task. */
export interface IDeliverySourceKey {
  scheduleNumber: string;
  taskId: string;
}

/** A task link as stored, and as a create posts it: the task and its SHARE
 *  of the line — what it put there when the order was raised plus every join
 *  it made since (ruling 95). Positive, one decimal. The LINE's `quantity`
 *  stays the ordered amount, which a person may edit, so the shares are not
 *  held to sum to it. */
export interface IDeliverySource extends IDeliverySourceKey {
  quantity: number;
}

/** The claim on the source task whose material matches this line's item. */
export interface IDeliverySourceBooked {
  quantity: number;
  unit: string;
  /** Per element kind, how many elements the claim holds; [] for free text. */
  elements: { kind: string; count: number }[];
  floorIds: string[];
}

/** The source task as it reads now (not as it read when the order was raised). */
export interface IDeliverySourceTask {
  name: string;
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
  /** Schedule group name, then ancestor task names, root first. */
  path: string[];
  status: TaskStatus;
  progressPercent: number;
  booked: IDeliverySourceBooked | null;
}

/** One source of a LINE, resolved server-side. `task` is null once the task
 *  (or its schedule) no longer exists. */
export interface IDeliveryLineSource extends IDeliverySource {
  task: IDeliverySourceTask | null;
}

/** A column of the delivery board. Per-site, user-managed; `system` marks the
 *  locked Delivered column (no rename, no delete). */
export type DeliveryBoardStatus = {
  id: string;
  name: string;
  system: boolean;
};

/** The board's one system column id. Mirrors the backend's
 *  `DELIVERED_STATUS_ID` (`core/logistics/types.ts`) — the id a site cannot
 *  rename or delete, and the only one that means anything beyond "which
 *  column is this card in". */
export const DELIVERED_STATUS_ID = 'delivered';

// Mirrors backend core/inventory InventoryLevel field-for-field — fully derived
// server-side, ready to render (see 2026-07-27-inventory-snapshot-v1-design.md).
export type InventoryLevel = {
  materialName: string;
  unit: string;
  amount: number;
  spanDays: number;
  dailyRate: number;
  daysLeft: number;
  needsCheck: boolean;
  enteredAt: string;
  enteredBy: string;
  /** The entry-time guess, echoed back so the stock dialog can prefill it. */
  estimatedLeadTimeDays: number | null;
  /** Effective delivery lead for this material (days, ~); null = no data, and
   *  then the row shows no lead and nothing can be overdue. */
  leadTimeDays: number | null;
  /** Where `leadTimeDays` came from — the three look identical as "~Nd", so the
   *  row must not let a guess (or an average of other materials) read as a
   *  measurement. null iff leadTimeDays is null. */
  leadSource: LeadSource | null;
  /** The lead the Sync dialog OFFERS (not the row's committed `leadTimeDays`).
   *  Optimistic: it measures the lead treating a delivered-but-unsynced order —
   *  or an in-flight order past its due date — as arrived, because clicking Sync
   *  is itself the signal it landed. `observed`/`site-median` only, never
   *  `estimated`. The row shows `leadTimeDays`; the dialog prefills from this. */
  suggestedLeadTimeDays: number | null;
  suggestedLeadSource: LeadSource | null;
  /** The one stock alert: at or past the reorder point, so ordering now arrives
   *  no earlier than the day the material runs out. `daysLeft <= leadTimeDays`
   *  — inclusive, because that day is the last day an order still lands.
   *  False when there is no lead time. */
  overdue: boolean;
  /** Data-driven span/cycle suggestion learned from past renewals of this
   *  material; null/0 when there isn't enough history yet (Task 9 renders
   *  these). */
  suggestedSpanDays: number | null;
  suggestionCycles: number;
  /** A delivery for this material exists and has not landed — the row shows
   *  "New delivery arriving" instead of the red order line. */
  deliveryIncoming: boolean;
  incomingEta: string | null;
  incomingEtaExact: boolean;
  /** A delivery for this material landed AFTER the current stock was entered,
   *  so the row asks "Stock ran out?". Stateless: renewing moves `enteredAt`
   *  past that `deliveredAt` and the flag clears itself. */
  awaitingRenewal: boolean;
  /** A due delivery nobody confirmed — only the user knows if it landed. */
  arrivalUnconfirmed: boolean;
  /** ISO instant of that arrival; null when none. */
  lastArrivalAt: string | null;
  /** Ref of the landed-but-unreconciled order behind `awaitingRenewal`, so the
   *  "new delivery arrived" line can point at it. Null when none. */
  lastArrivalRef: string | null;
  /** The due date (YYYY-MM-DD) the landed order was measured against, so a
   *  delivered row can still say how late it arrived. Null when no fresh arrival
   *  awaits reconciliation, or the order had no due date. */
  lastArrivalDueDate: string | null;
  /** Whether `lastArrivalDueDate` was supplier-confirmed (`answeredDate`) rather
   *  than a projection — "Due {d}" vs "Estimated {d}". */
  lastArrivalDueExact: boolean;
  /** Ref of the order `incomingEta` is about, so the line naming a delivery can
   *  also point at it. */
  incomingRef: string | null;
  /** Ref of an order raised for this material but never sent. A draft does not
   *  count as incoming — the red prompt stays — but naming it lets the prompt
   *  say what is missing instead of repeating itself at someone who just
   *  used it. */
  draftOrderRef: string | null;
};

/** A material that exists on the delivery side only — see `untracked`. */
/**
 * Where a level's effective lead time came from. Named because all three render
 * as an identical "~Nd" — a borrowed site average must not read as a
 * measurement of this material. Mirrors the backend's `LeadSource`.
 */
export type LeadSource = 'observed' | 'estimated' | 'site-median';

export type UntrackedMaterial = {
  materialName: string;
  /** From the delivery LINE for this material. Null when the line recorded
   *  none — never another line's amount. */
  quantity: number | null;
  unit: string | null;
  /** The order whose line introduced it — the newest one. */
  deliveryRef: string;
  /** ISO instant it landed; null while still in flight. The sole arrival fact —
   *  there is no workflow status to consult. */
  deliveredAt: string | null;
  /** Sync is the commit point — locked status + one-unsynced-order UI. */
  isSynced: boolean;
  /** The day it is expected. Decides whether the row asks about an arrival:
   *  an order still in the future is nothing to count yet. */
  dueDate: string | null;
  /** What this material's own deliveries measure, or the site median when they
   *  cannot. Seeds the Add-stock dialog — leads are otherwise read off the
   *  levels, and an untracked material has none. */
  leadTimeDays: number | null;
  leadSource: 'observed' | 'site-median' | null;
};

export type SiteZone = {
  zoneCode: string;
  type: ZoneType;
  status: ZoneStatus;
  count: number;
};

export type RestockAlert = {
  materialCode: string;
  daysToDeplete: number;
  nextPourDays: number;
};

/** One Waste-card row — a material's loss summed over every stock cycle that
 *  recorded one (the Sync dialog's Waste field). Derived server-side. */
export type WasteEntry = {
  materialName: string;
  unit: string;
  totalWaste: number;
  /** Closed cycles that contributed a figure. */
  cycles: number;
  /** ISO instant of the latest contributing sync. */
  lastSyncedAt: string;
  /** Every contributing cycle, oldest first. */
  history: WasteCycle[];
  /** Null only when no cycle has closed with recorded waste, or when the
   *  one (or only) closed cycle's span is zero. */
  projection: WasteProjection | null;
};

/** One closed cycle as the Waste card's history shows it. */
export type WasteCycle = {
  openedAt: string;
  closedAt: string;
  /** Null on cycles closed before the leftover count was kept. */
  startingStock: number | null;
  left: number | null;
  waste: number;
  /** The recorded figure is the dialog's own seed — nobody corrected it. */
  estimated: boolean;
  /** Less than a tenth of the batch was left, so the seed was the leftover. */
  capped: boolean;
};

export type WasteProjection = {
  perCycle: number;
  in6Months: number;
  in12Months: number;
};

export type LogisticsKpis = {
  activeDeliveries: number;
  stockAlerts: number;
};

export type SiteLogistics = {
  projectId: string;
  /** Monotonic logistics revision; writes send it so a stale editor is refused. */
  revision: number;
  kpis: LogisticsKpis;
  deliveries: Delivery[];
  inventory: InventoryLevel[];
  materialSuggestions: string[];
  /** Materials with a delivery order but no stock record — derived on every
   *  read, never stored. They render as placeholder rows at the top of
   *  Inventory Levels and vanish the moment a real snapshot exists. */
  untracked: UntrackedMaterial[];
  /** Median observed lead across the site, or null when nothing has been
   *  delivered here yet. The stock dialog falls back to it whenever the
   *  material in hand has no lead of its own — including the blank "Add stock",
   *  where there is no material yet at all. */
  siteMedianLeadDays: number | null;
  zones: SiteZone[];
  restockAlert: RestockAlert | null;
  waste: WasteEntry[];
  asOf: string;
  /** Whether the caller's role may change logistics data. Computed
   *  server-side (the same rule the mutations enforce with 403), so the UI
   *  never derives rights from client state; false renders the page
   *  read-only — every write control disabled, every record still openable. */
  canEdit: boolean;
};
